import { Link } from 'react-router';
import { Bug, CalendarClock, LifeBuoy } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/constants';
import { usePortfolio } from '@/services/queries';
import { JumpButton } from '@/shared/ui/section-heading';
import { Skeleton } from '@/shared/ui/skeleton';
import type { PortfolioSummary } from '@/types';
import { Sparkline, type SparkTone } from './Sparkline';

/**
 * The design's gradient banner: a heading on the left, three white stat tiles,
 * and a jump button on the right.
 *
 * Transcribed - a 135° gradient from brand-400 to brand-600 at 14px radius, the
 * heading in a fixed 130px column with a translucent rule under it, and the
 * tiles at 10px radius with their own 4px accent bar flush to the bottom.
 *
 * ── Why a coloured band in the middle of the page ───────────────────────────
 *
 * Because it is the one thing on this screen that is about time rather than
 * about a lab. Everything above and below is "which organisation" - this is
 * "what is happening this week regardless of who". Making it a different
 * surface is what stops it reading as a fourth row of cards.
 *
 * The tiles stay white on the gradient rather than becoming translucent: these
 * are numbers to read, and a number at 28px on a mid-blue ground is a number
 * somebody squints at.
 */
interface Tile {
  key: string;
  label: string;
  value: number;
  icon: LucideIcon;
  /** What it means, under the number. */
  note: string;
  /** Where the tile leads. Omitted where the admin portal has no page for it
   *  yet - a tile that links somewhere unrelated is worse than one that
   *  does not link. */
  to?: string;
  trend: keyof Omit<PortfolioSummary['trends'], 'stepDays'>;
  spark: SparkTone;
}

export function CriticalThisWeek() {
  const query = usePortfolio(4);
  const summary = query.data;

  const week = summary?.thisWeek;

  const tiles: Tile[] = [
    {
      key: 'expiring',
      label: 'Expiring',
      value: week?.expiring ?? 0,
      icon: CalendarClock,
      note: 'licences in the next 7 days',
      to: ROUTES.customers,
      trend: 'expiringThisWeek',
      spark: 'licensing',
    },
    {
      key: 'issues',
      label: 'Issues',
      value: week?.issues ?? 0,
      icon: LifeBuoy,
      note: 'raised in the last 7 days',
      trend: 'issuesThisWeek',
      spark: 'licensing',
    },
    {
      key: 'crashes',
      label: 'Crashes',
      value: week?.crashes ?? 0,
      icon: Bug,
      note: 'reported in the last 7 days',
      trend: 'crashesThisWeek',
      spark: 'crash',
    },
  ];

  return (
    <section className="flex flex-wrap items-center gap-3.5 rounded-[14px] bg-linear-[135deg,var(--color-brand-400),var(--color-brand-600)] px-[22px] py-5">
      <h2 className="flex-none basis-32.5 border-b-2 border-white/40 pb-2 text-[17px] font-bold text-white">
        Critical this week
      </h2>

      <div className="flex min-w-0 flex-1 flex-wrap gap-3.5">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          const content = (
            <>
              <span
                className="flex size-5 flex-none items-center justify-center rounded-md bg-brand-50"
                aria-hidden="true"
              >
                <Icon size={11} strokeWidth={2.3} className="text-brand-600" />
              </span>
              <span className="mt-2 mb-1.5 border-b border-neutral-200 pb-1.5 text-xs font-semibold text-neutral-900">
                {tile.label}
              </span>
              {query.isPending ? (
                <Skeleton className="mb-2.5 h-7 w-12" />
              ) : (
                <span className="text-[28px] leading-none font-bold tracking-[-0.8px] text-neutral-900 tabular-nums">
                  {tile.value}
                </span>
              )}
              <span className="mt-2 mb-2 text-[11px] text-neutral-500">{tile.note}</span>
              {summary && (
                <Sparkline
                  className="mb-2.5"
                  points={summary.trends[tile.trend]}
                  stepDays={summary.trends.stepDays}
                  tone={tile.spark}
                  label={tile.label}
                  surface="white"
                />
              )}
              {/*
                Full-bleed accent, the same idea as AccentCard's - the tile has
                no bottom padding so the bar is part of its edge. Not the shared
                component, because these sit on the gradient at their own scale.
              */}
              <span className="-mx-4 mt-auto h-1 bg-brand-600" aria-hidden="true" />
            </>
          );
          const className =
            'flex min-w-50 flex-1 flex-col overflow-hidden rounded-[10px] bg-white px-4 pt-3.5 shadow-[0_4px_10px_rgba(0,0,0,0.15)]';

          return tile.to ? (
            <Link
              key={tile.key}
              to={tile.to}
              className={`${className} transition-transform hover:-translate-y-px`}
            >
              {content}
            </Link>
          ) : (
            <div key={tile.key} className={className}>
              {content}
            </div>
          );
        })}
      </div>

      <JumpButton
        label="View all critical items"
        tone="dark"
        render={(content, className) => (
          <Link to={ROUTES.customers} className={className}>
            {content}
          </Link>
        )}
      />
    </section>
  );
}
